// frontend/src/pages/yard/YardLocationsAdmin.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import TypeAhead, { TypeAheadOption } from '../../components/TypeAhead';
import { useToast } from '../../contexts/ToastContext';
import yardService from '../../services/yard.service';
import { YardLocationAdmin, YardLocationLink, MasterLocation } from '../../types/yard';

// Sentinel recno for the in-progress "new yard" row pinned at the top.
const NEW_RECNO = -1;

type NewRowDraft = {
  yard: string;
  links: YardLocationLink[];
};

export default function YardLocationsAdmin() {
  const { showToast } = useToast();

  const [rows, setRows] = useState<YardLocationAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);

  const [masterLocations, setMasterLocations] = useState<MasterLocation[]>([]);
  const [savingRowRecno, setSavingRowRecno] = useState<number | null>(null);

  // Inline edit state for the yard-name column.
  const [editingRecno, setEditingRecno] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  // Which row currently has the "+ Add" picker expanded inline.
  const [addingLinkRecno, setAddingLinkRecno] = useState<number | null>(null);

  // Unsaved new-yard row.
  const [newRow, setNewRow] = useState<NewRowDraft | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await yardService.listYardLocationsAdmin(true);
      setRows(data);
    } catch (err: any) {
      showToast({ message: err.response?.data?.detail || 'Failed to load yard locations', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    yardService
      .getMasterLocations()
      .then(setMasterLocations)
      .catch((err: any) => {
        showToast({ message: err.response?.data?.detail || 'Failed to load locations', type: 'error' });
      });
  }, [showToast]);

  const filteredRows = useMemo<YardLocationAdmin[]>(() => {
    const visible = showDeleted ? rows : rows.filter((r) => r.deleted !== 'Y');
    if (newRow) {
      const draft: YardLocationAdmin = {
        recno: NEW_RECNO,
        yard: newRow.yard,
        deleted: 'N',
        links: newRow.links,
        last_event: null,
      };
      return [draft, ...visible];
    }
    return visible;
  }, [rows, showDeleted, newRow]);

  const masterLocationOptions = useMemo<TypeAheadOption[]>(
    () =>
      masterLocations.map((loc) => {
        const place = [loc.lcity, loc.lstate].filter(Boolean).join(', ');
        const display = loc.cyalias || loc.lname;
        return {
          value: String(loc.recno),
          label: display,
          description: [loc.lname !== display ? loc.lname : null, place || null]
            .filter(Boolean)
            .join(' • '),
        };
      }),
    [masterLocations],
  );

  const masterLocationsById = useMemo(() => {
    const m = new Map<number, MasterLocation>();
    masterLocations.forEach((loc) => m.set(loc.recno, loc));
    return m;
  }, [masterLocations]);

  const startEdit = (row: YardLocationAdmin) => {
    setEditingRecno(row.recno);
    setEditingName(row.yard);
  };

  const cancelEdit = () => {
    setEditingRecno(null);
    setEditingName('');
  };

  const saveEdit = async (row: YardLocationAdmin) => {
    const trimmed = editingName.trim();
    if (!trimmed) {
      showToast({ message: 'Yard name is required', type: 'error' });
      return;
    }
    if (trimmed === row.yard) {
      cancelEdit();
      return;
    }
    setSavingRowRecno(row.recno);
    try {
      const updated = await yardService.updateYardLocation(row.recno, { yard: trimmed });
      setRows((prev) => prev.map((r) => (r.recno === row.recno ? updated : r)));
      showToast({ message: 'Yard renamed', type: 'success', duration: 2000 });
      cancelEdit();
    } catch (err: any) {
      showToast({ message: err.response?.data?.detail || 'Save failed', type: 'error' });
    } finally {
      setSavingRowRecno(null);
    }
  };

  const handleDelete = async (row: YardLocationAdmin) => {
    if (!confirm(`Soft-delete "${row.yard}"? It will be hidden from yard pickers.`)) return;
    setSavingRowRecno(row.recno);
    try {
      const updated = await yardService.softDeleteYardLocation(row.recno);
      setRows((prev) => prev.map((r) => (r.recno === row.recno ? updated : r)));
      showToast({ message: 'Yard deleted', type: 'success', duration: 2000 });
    } catch (err: any) {
      showToast({ message: err.response?.data?.detail || 'Delete failed', type: 'error' });
    } finally {
      setSavingRowRecno(null);
    }
  };

  const handleRestore = async (row: YardLocationAdmin) => {
    setSavingRowRecno(row.recno);
    try {
      const updated = await yardService.restoreYardLocation(row.recno);
      setRows((prev) => prev.map((r) => (r.recno === row.recno ? updated : r)));
      showToast({ message: 'Yard restored', type: 'success', duration: 2000 });
    } catch (err: any) {
      showToast({ message: err.response?.data?.detail || 'Restore failed', type: 'error' });
    } finally {
      setSavingRowRecno(null);
    }
  };

  const startNewRow = () => {
    setNewRow({ yard: '', links: [] });
    setEditingRecno(NEW_RECNO);
    setEditingName('');
  };

  const cancelNewRow = () => {
    setNewRow(null);
    setAddingLinkRecno((cur) => (cur === NEW_RECNO ? null : cur));
    if (editingRecno === NEW_RECNO) {
      cancelEdit();
    }
  };

  const saveNewRow = async () => {
    if (!newRow) return;
    const trimmed = editingName.trim();
    if (!trimmed) {
      showToast({ message: 'Yard name is required', type: 'error' });
      return;
    }
    setSavingRowRecno(NEW_RECNO);
    try {
      const ids = newRow.links.map((l) => l.location_id);
      const created = await yardService.createYardLocation(trimmed, ids);
      setRows((prev) => [...prev, created]);
      setNewRow(null);
      setAddingLinkRecno((cur) => (cur === NEW_RECNO ? null : cur));
      cancelEdit();
      showToast({ message: 'Yard created', type: 'success', duration: 2000 });
    } catch (err: any) {
      showToast({ message: err.response?.data?.detail || 'Create failed', type: 'error' });
    } finally {
      setSavingRowRecno(null);
    }
  };

  // Build a YardLocationLink from the master location options for the new-row
  // draft (so chips render the same display info before save).
  const buildLinkFromMaster = useCallback(
    (locationId: number): YardLocationLink => {
      const loc = masterLocationsById.get(locationId);
      const display = loc ? (loc.cyalias || loc.lname) : `#${locationId}`;
      return {
        location_id: locationId,
        location_name: display,
        cyalias: loc?.cyalias || null,
        lname: loc?.lname || null,
        lcity: loc?.lcity || null,
        lstate: loc?.lstate || null,
      };
    },
    [masterLocationsById],
  );

  const addLink = useCallback(
    async (recno: number, locationId: number) => {
      if (recno === NEW_RECNO) {
        setNewRow((prev) => {
          if (!prev) return prev;
          if (prev.links.some((l) => l.location_id === locationId)) return prev;
          const next = [...prev.links, buildLinkFromMaster(locationId)];
          next.sort((a, b) => a.location_name.localeCompare(b.location_name));
          return { ...prev, links: next };
        });
        setAddingLinkRecno(null);
        return;
      }
      setSavingRowRecno(recno);
      try {
        const updated = await yardService.addYardLocationLink(recno, locationId);
        setRows((prev) => prev.map((r) => (r.recno === recno ? updated : r)));
        setAddingLinkRecno(null);
      } catch (err: any) {
        showToast({ message: err.response?.data?.detail || 'Failed to add link', type: 'error' });
      } finally {
        setSavingRowRecno(null);
      }
    },
    [buildLinkFromMaster, showToast],
  );

  const removeLink = useCallback(
    async (recno: number, locationId: number) => {
      if (recno === NEW_RECNO) {
        setNewRow((prev) =>
          prev ? { ...prev, links: prev.links.filter((l) => l.location_id !== locationId) } : prev,
        );
        return;
      }
      setSavingRowRecno(recno);
      try {
        const updated = await yardService.removeYardLocationLink(recno, locationId);
        setRows((prev) => prev.map((r) => (r.recno === recno ? updated : r)));
      } catch (err: any) {
        showToast({ message: err.response?.data?.detail || 'Failed to remove link', type: 'error' });
      } finally {
        setSavingRowRecno(null);
      }
    },
    [showToast],
  );

  const columns = useMemo<ColumnDef<YardLocationAdmin>[]>(() => [
    {
      accessorKey: 'recno',
      header: 'ID',
      size: 80,
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ row }) =>
        row.original.recno === NEW_RECNO ? (
          <span className="text-blue-600 dark:text-blue-400 italic">new</span>
        ) : (
          row.original.recno
        ),
    },
    {
      accessorKey: 'yard',
      header: 'Yard Name',
      size: 280,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const r = row.original;
        const isEditing = editingRecno === r.recno;
        if (isEditing) {
          return (
            <input
              type="text"
              autoFocus
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (r.recno === NEW_RECNO) saveNewRow();
                  else saveEdit(r);
                } else if (e.key === 'Escape') {
                  if (r.recno === NEW_RECNO) cancelNewRow();
                  else cancelEdit();
                }
              }}
              className="w-full border rounded px-2 py-1 text-sm"
              maxLength={255}
              placeholder="Yard name"
            />
          );
        }
        return <span className="font-medium">{r.yard}</span>;
      },
    },
    {
      id: 'links',
      accessorFn: (r) => r.links.map((l) => l.location_name).join(', '),
      header: 'Linked Locations',
      size: 380,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const r = row.original;
        const isAdding = addingLinkRecno === r.recno;
        const isReadOnly = r.deleted === 'Y';
        const linkedIds = new Set(r.links.map((l) => l.location_id));
        const pickerOptions = masterLocationOptions.filter((opt) => !linkedIds.has(Number(opt.value)));
        const isSaving = savingRowRecno === r.recno && editingRecno !== r.recno;

        return (
          <div className="flex flex-wrap items-center gap-1 w-full">
            {r.links.map((link) => {
              const place = [link.lcity, link.lstate].filter(Boolean).join(', ');
              const tooltip = [link.lname !== link.location_name ? link.lname : null, place || null]
                .filter(Boolean)
                .join(' — ');
              return (
                <span
                  key={link.location_id}
                  title={tooltip || link.location_name}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 text-xs"
                >
                  <span className="truncate max-w-[180px]">{link.location_name}</span>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => removeLink(r.recno, link.location_id)}
                      disabled={isSaving}
                      className="hover:text-blue-950 dark:hover:text-white disabled:opacity-50"
                      title="Remove link"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  )}
                </span>
              );
            })}

            {!isReadOnly && (
              isAdding ? (
                <div className="flex items-center gap-1 min-w-[220px] flex-1">
                  <div className="flex-1 min-w-0">
                    <TypeAhead
                      options={pickerOptions}
                      value=""
                      onChange={(v) => {
                        if (v) addLink(r.recno, Number(v));
                      }}
                      placeholder="Search locations..."
                      showDescriptions
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setAddingLinkRecno(null)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Cancel"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingLinkRecno(r.recno)}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-dashed border-gray-400 text-gray-600 hover:text-blue-700 hover:border-blue-500 text-xs disabled:opacity-50"
                  title={r.links.length === 0 ? 'Add linked location' : 'Add another linked location'}
                >
                  <PlusIcon className="w-3 h-3" />
                  Add
                </button>
              )
            )}

            {isSaving && (
              <ArrowPathIcon className="w-4 h-4 animate-spin text-blue-500 shrink-0" />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'last_event',
      header: 'Last Performed',
      size: 170,
      enableSorting: true,
      enableColumnFilter: true,
      sortingFn: 'datetime',
      cell: ({ row }) => {
        const v = row.original.last_event;
        if (!v) return <span className="text-gray-400">-</span>;
        const d = new Date(v);
        if (isNaN(d.getTime())) return <span className="text-gray-400">-</span>;
        return (
          <span>
            {d.toLocaleString('en-US', {
              month: 'numeric',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </span>
        );
      },
    },
    {
      accessorKey: 'deleted',
      header: 'Status',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        if (row.original.recno === NEW_RECNO) {
          return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">New</span>;
        }
        return row.original.deleted === 'Y' ? (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Deleted</span>
        ) : (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Active</span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const r = row.original;
        const isEditing = editingRecno === r.recno;
        const isSaving = savingRowRecno === r.recno;
        const isNew = r.recno === NEW_RECNO;
        const isDeleted = r.deleted === 'Y';

        if (isEditing) {
          return (
            <div className="flex items-center gap-1">
              <button
                onClick={() => (isNew ? saveNewRow() : saveEdit(r))}
                disabled={isSaving}
                className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                title="Save"
              >
                {isSaving ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckIcon className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => (isNew ? cancelNewRow() : cancelEdit())}
                disabled={isSaving}
                className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                title="Cancel"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          );
        }

        return (
          <div className="flex items-center gap-1">
            <button
              onClick={() => startEdit(r)}
              className="p-1 text-blue-600 hover:text-blue-800"
              title="Rename"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            {isDeleted ? (
              <button
                onClick={() => handleRestore(r)}
                disabled={isSaving}
                className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                title="Restore"
              >
                {isSaving ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowUturnLeftIcon className="w-4 h-4" />
                )}
              </button>
            ) : (
              <button
                onClick={() => handleDelete(r)}
                disabled={isSaving}
                className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                title="Delete"
              >
                {isSaving ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : (
                  <TrashIcon className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        );
      },
    },
  ], [
    masterLocationOptions,
    addingLinkRecno,
    addLink,
    removeLink,
    savingRowRecno,
    editingRecno,
    editingName,
  ]);

  return (
    <div className="h-full w-full flex flex-col px-4 pt-4 pb-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h1 className="text-2xl font-bold dark:text-white">Yard Inventory Locations</h1>
        <button
          type="button"
          onClick={startNewRow}
          disabled={!!newRow}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <PlusIcon className="w-4 h-4" />
          Add Yard
        </button>
      </div>

      <div className="flex-1 min-h-0">
        <TanTable
          data={filteredRows}
          columns={columns}
          loading={loading}
          settingsKey="yard-locations-admin"
          fieldName="yard-locations-admin"
          cardTitle="Yard Inventory Locations"
          searchPlaceholder="Search by yard name..."
          enableSearch
          enableColumnFilters
          enableExcelExport
          enableShowHideCols
          enableZebraStriping={false}
          showDeletedToggle
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          fillContainer
        />
      </div>
    </div>
  );
}
